import React, { FC, ReactElement, useContext, useMemo } from 'react'
import moment from 'moment'
import { SummaryInformationDTO } from '../../../../React/InsightsHub/Types/Types'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext } from '../../../../React/Common/Contexts'
import { InsightsStat, InsightsStatProps } from './InsightsStat'
import { IconName } from '../../../../React/Common/Components/Icon'
import { InsightsDashboardLanguage } from '../../../../../i18n'
import { useFeatureFlag } from '../../../../React/Common/Hooks/useFeatureFlag'
import { ChartData } from '../../../../NonReact/Types/Types'
import { convertDurationInSecondsToFormattedString } from '../../../../React/Common/Utils/TimeFormats'

type Props = {
    summaryInfo: Nullable<SummaryInformationDTO>
}

const formatDate = (date: number): string => moment(date).format("Do MMM 'YY")

export const InsightsStats: FC<Props> = ({ summaryInfo }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler

    const isLongestWaitEnabled: Definable<boolean> = useFeatureFlag('insightsHub.showLongestWaitTime')

    const asString = (value: number): string => convertDurationInSecondsToFormattedString(value, language)

    const summaryStats: Nullable<InsightsStatProps[]> = useMemo(
        (): Nullable<InsightsStatProps[]> => [
            {
                className: 'idiss-stat-1',
                iconName: IconName.CALLS_RECEIVED,
                title: language.get(InsightsDashboardLanguage.InsightsDashboard_CallsReceived),
                value: summaryInfo?.CallsReceived?.Value ?? 0,
                vsValue: Math.round(Number(summaryInfo?.CallsReceived?.PercentageChg ?? 0)),
                isPositiveVsValueGood: true,
                isVsPercentage: true,
                graphData: Object.entries(summaryInfo?.CallsReceived?.GraphData ?? {})?.map(
                    ([date, graphValue]: [string, number]): ChartData => ({
                        name: formatDate(Number(date)),
                        value: graphValue
                    })
                ),
                weeklyVs: {
                    thisWeek: summaryInfo?.CallsReceived?.WeeklyData?.ThisWeek ?? 0,
                    lastWeek: summaryInfo?.CallsReceived?.WeeklyData?.LastWeek ?? 0,
                    prevWeek: summaryInfo?.CallsReceived?.WeeklyData?.PreviousWeek ?? 0
                }
            },
            {
                className: 'idiss-stat-2',
                iconName: IconName.MISSED_CALLS,
                title: language.get(InsightsDashboardLanguage.InsightsDashboard_MissedCalls),
                value: summaryInfo?.MissedCalls?.Value ?? 0,
                vsValue: Math.round(Number(summaryInfo?.MissedCalls?.PercentageChg ?? 0)),
                isPositiveVsValueGood: false,
                isVsPercentage: true,
                graphData: Object.entries(summaryInfo?.MissedCalls?.GraphData ?? {})?.map(
                    ([date, graphValue]: [string, number]): ChartData => ({
                        name: formatDate(Number(date)),
                        value: graphValue
                    })
                ),
                weeklyVs: {
                    thisWeek: summaryInfo?.MissedCalls?.WeeklyData?.ThisWeek ?? 0,
                    lastWeek: summaryInfo?.MissedCalls?.WeeklyData?.LastWeek ?? 0,
                    prevWeek: summaryInfo?.MissedCalls?.WeeklyData?.PreviousWeek ?? 0
                }
            },
            {
                className: 'idiss-stat-3',
                iconName: IconName.AVERAGE_ANSWER,
                title: language.get(InsightsDashboardLanguage.InsightsDashboard_AverageAnswer),
                value: summaryInfo?.AverageAnswerTime?.Value ?? 0,
                asString,
                vsValue: Math.round(Number(summaryInfo?.AverageAnswerTime?.ValueChg ?? 0)),
                isPositiveVsValueGood: false,
                isVsPercentage: false,
                graphData: Object.entries(summaryInfo?.AverageAnswerTime?.GraphData ?? {})?.map(
                    ([date, graphValue]: [string, number]): ChartData => ({
                        name: formatDate(Number(date)),
                        value: graphValue
                    })
                ),
                weeklyVs: {
                    thisWeek: summaryInfo?.AverageAnswerTime?.WeeklyData?.ThisWeek ?? 0,
                    lastWeek: summaryInfo?.AverageAnswerTime?.WeeklyData?.LastWeek ?? 0,
                    prevWeek: summaryInfo?.AverageAnswerTime?.WeeklyData?.PreviousWeek ?? 0
                }
            },
            {
                className: 'idiss-stat-4',
                iconName: IconName.AVERAGE_ABANDONED,
                title: language.get(InsightsDashboardLanguage.InsightsDashboard_AverageAbandoned),
                value: summaryInfo?.AverageAbandonedTime?.Value ?? 0,
                asString,
                vsValue: Math.round(Number(summaryInfo?.AverageAbandonedTime?.ValueChg ?? 0)),
                isPositiveVsValueGood: true,
                isVsPercentage: false,
                graphData: Object.entries(summaryInfo?.AverageAbandonedTime?.GraphData ?? {})?.map(
                    ([date, graphValue]: [string, number]): ChartData => ({
                        name: formatDate(Number(date)),
                        value: graphValue
                    })
                ),
                weeklyVs: {
                    thisWeek: summaryInfo?.AverageAbandonedTime?.WeeklyData?.ThisWeek ?? 0,
                    lastWeek: summaryInfo?.AverageAbandonedTime?.WeeklyData?.LastWeek ?? 0,
                    prevWeek: summaryInfo?.AverageAbandonedTime?.WeeklyData?.PreviousWeek ?? 0
                }
            },
            ...(isLongestWaitEnabled
                ? [
                      {
                          className: 'idiss-stat-5',
                          iconName: IconName.LONGEST_WAIT,
                          title: language.get(InsightsDashboardLanguage.InsightsDashboard_LongestWait),
                          value: summaryInfo?.LongestWaitTime?.Value ?? 0,
                          asString,
                          vsValue: Math.round(Number(summaryInfo?.LongestWaitTime?.ValueChg ?? 0)),
                          isPositiveVsValueGood: false,
                          isVsPercentage: false,
                          graphData: Object.entries(summaryInfo?.LongestWaitTime?.GraphData ?? {})?.map(
                              ([date, graphValue]: [string, number]): ChartData => ({
                                  name: formatDate(Number(date)),
                                  value: graphValue
                              })
                          ),
                          weeklyVs: {
                              thisWeek: summaryInfo?.LongestWaitTime?.WeeklyData?.ThisWeek ?? 0,
                              lastWeek: summaryInfo?.LongestWaitTime?.WeeklyData?.LastWeek ?? 0,
                              prevWeek: summaryInfo?.LongestWaitTime?.WeeklyData?.PreviousWeek ?? 0
                          }
                      }
                  ]
                : [])
        ],
        [summaryInfo, isLongestWaitEnabled]
    )

    return (
        <div className='id-stats id-summary-info'>
            {summaryStats?.map(
                (stat: InsightsStatProps, index: number): ReactElement => (
                    <InsightsStat key={index} {...stat} index={index} />
                )
            )}
        </div>
    )
}
